import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Multi-select for the Projects area, following desktop file-manager
 * conventions:
 *
 *   plain click        open the item (unchanged) — unless a selection is
 *                      already active, in which case it toggles, so you can
 *                      keep building a selection without holding a modifier
 *   Cmd/Ctrl + click   toggle one item
 *   Shift + click      select the range between the anchor and the clicked
 *                      item, within the same kind (folders and projects are
 *                      separate lists on screen, so a range never spans both)
 *   Cmd/Ctrl + A       select everything currently in view
 *   Escape             clear
 *
 * Selections are keyed by id and pruned whenever the visible ids change, so a
 * deleted or filtered-out item can never linger in a bulk action.
 */
export default function useProjectSelection({ projectIds, folderIds }) {
  const [selectedProjects, setSelectedProjects] = useState(() => new Set());
  const [selectedFolders, setSelectedFolders] = useState(() => new Set());
  const anchorRef = useRef({ type: null, id: null });

  const projectKey = projectIds.join(",");
  const folderKey = folderIds.join(",");

  // Drop ids that are no longer on screen (deleted, filtered, folder opened).
  useEffect(() => {
    setSelectedProjects((prev) => {
      const visible = new Set(projectIds);
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectKey]);

  useEffect(() => {
    setSelectedFolders((prev) => {
      const visible = new Set(folderIds);
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderKey]);

  const totalSelected = selectedProjects.size + selectedFolders.size;
  const selectionActive = totalSelected > 0;

  const clear = useCallback(() => {
    setSelectedProjects(new Set());
    setSelectedFolders(new Set());
    anchorRef.current = { type: null, id: null };
  }, []);

  const selectAll = useCallback(() => {
    setSelectedProjects(new Set(projectIds));
    setSelectedFolders(new Set(folderIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectKey, folderKey]);

  const setFor = (type) =>
    type === "folder" ? setSelectedFolders : setSelectedProjects;
  const idsFor = (type) => (type === "folder" ? folderIds : projectIds);

  const toggle = useCallback((type, id) => {
    setFor(type)((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    anchorRef.current = { type, id };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectRangeTo = useCallback(
    (type, id) => {
      const list = idsFor(type);
      const anchor = anchorRef.current;
      const from =
        anchor.type === type ? list.indexOf(anchor.id) : -1;
      const to = list.indexOf(id);
      if (to === -1) return;

      // No usable anchor yet — treat Shift+click as a plain select.
      if (from === -1) {
        setFor(type)((prev) => new Set(prev).add(id));
        anchorRef.current = { type, id };
        return;
      }

      const [lo, hi] = from < to ? [from, to] : [to, from];
      const range = list.slice(lo, hi + 1);
      setFor(type)((prev) => {
        const next = new Set(prev);
        range.forEach((r) => next.add(r));
        return next;
      });
      // Anchor stays put so shift-clicking again re-ranges from the same origin.
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectKey, folderKey]
  );

  /**
   * Returns true when the click was consumed as a selection gesture, meaning
   * the caller should not perform its default open action.
   */
  const handleItemClick = useCallback(
    (e, type, id) => {
      if (e.shiftKey) {
        selectRangeTo(type, id);
        return true;
      }
      if (e.metaKey || e.ctrlKey) {
        toggle(type, id);
        return true;
      }
      if (selectionActive) {
        toggle(type, id);
        return true;
      }
      return false;
    },
    [selectionActive, toggle, selectRangeTo]
  );

  const isSelected = useCallback(
    (type, id) =>
      type === "folder" ? selectedFolders.has(id) : selectedProjects.has(id),
    [selectedFolders, selectedProjects]
  );

  // Keyboard: Escape clears, Cmd/Ctrl+A selects all — but never while the user
  // is typing in the search box or renaming a folder.
  useEffect(() => {
    const isTextEntry = (el) =>
      el &&
      (el.tagName === "INPUT" ||
        el.tagName === "TEXTAREA" ||
        el.isContentEditable);

    const onKeyDown = (e) => {
      if (isTextEntry(document.activeElement)) return;
      if (e.key === "Escape" && selectionActive) {
        clear();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "a") {
        if (!projectIds.length && !folderIds.length) return;
        e.preventDefault();
        selectAll();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectionActive, clear, selectAll, projectKey, folderKey]);

  return {
    selectedProjects,
    selectedFolders,
    selectedProjectIds: [...selectedProjects],
    selectedFolderIds: [...selectedFolders],
    totalSelected,
    selectionActive,
    isSelected,
    handleItemClick,
    toggle,
    clear,
    selectAll,
  };
}
